/* ═══════════════════════════════════════════════════════
   ANTINORM – Quiz Tracking  v2
   File: assets/antinorm-quiz-tracking.js

   Loads BEFORE antinorm-quiz.js and exposes window.ANQ_TRACK.
   All GA4 event parameter names are prefixed with "quiz_"
   to avoid conflicts with GA4 reserved names.

   Three data layers:
   1. GA4 custom events  — always fires (anonymous + named if logged in)
   2. Cart attributes    — fires on quiz complete, appears on every order
   3. Customer metafield — logged-in users only, via Shopify Flow on purchase
═══════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var DEBUG = true;

  function log() {
    if (DEBUG && window.console && console.log) {
      console.log.apply(console, ['[ANQ Track]'].concat(Array.prototype.slice.call(arguments)));
    }
  }

  function getCustomer() {
    try {
      var c = window.Shopify && window.Shopify.customer;
      if (c && c.id) {
        return {
          id:    String(c.id),
          email: c.email || '',
          name:  (c.first_name || '') + (c.last_name ? ' ' + c.last_name : '')
        };
      }
    } catch (e) { log('getCustomer error', e); }
    return null;
  }

  function gaPush(eventName, params) {
    try {
      var customer = getCustomer();

      var payload = Object.assign({}, {
        event_category: 'antinorm_quiz',
        quiz_version:   'v9',
        page_path:      window.location.pathname
      }, params);

      if (customer) {
        payload.quiz_customer_id    = customer.id;
        payload.quiz_customer_email = customer.email;
        payload.quiz_customer_name  = customer.name;
      }

      log('Event:', eventName, payload);

      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, quiz_data: payload });
        log('Sent via dataLayer');
      }

      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
        log('Sent via gtag');
      }

      if (!window.dataLayer && typeof window.gtag !== 'function') {
        log('WARNING: Neither dataLayer nor gtag found. Is GA4 installed on this store?');
      }

    } catch (e) {
      log('gaPush error', e);
    }
  }

  function writeCartAttributes(data) {
    var attrs = {
      'quiz_skin':           (data.skin      || []).join(', '),
      'quiz_hair':           (data.hair      || []).join(', '),
      'quiz_age':            data.age        || '',          /* ← added */
      'quiz_lifestyle':      (data.lifestyle || []).join(', '),
      'quiz_persona':        data.persona    || '',
      'quiz_products_shown': (data.productsShown || []).join(', '),
      'quiz_completed_at':   new Date().toISOString(),
      'quiz_session_id':     data.sessionId  || ''
    };
    log('Writing cart attributes', attrs);
    fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({ attributes: attrs })
    })
    .then(function (r) { log('Cart attributes written, status:', r.status); })
    .catch(function (e) { log('Cart attributes error:', e); });
  }

  function writeCartNote(data) {
    var note = [
      'ANTINORM QUIZ — ' + new Date().toLocaleDateString('en-IN'),
      'Skin: '      + (data.skin      || []).join(', '),
      'Hair: '      + (data.hair      || []).join(', '),
      'Age: '       + (data.age       || ''),               /* ← added */
      'Lifestyle: ' + (data.lifestyle || []).join(', '),
      'Persona: '   + (data.persona   || ''),
      'Shown: '     + (data.productsShown || []).join(', ')
    ].join(' | ');

    fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({ note: note })
    })
    .then(function () { log('Cart note written'); })
    .catch(function (e) { log('Cart note error:', e); });
  }

  function writeCustomerData(data) {
    var customer = getCustomer();
    if (!customer) { log('Not logged in — skipping customer metafield'); return; }
    log('Logged in as', customer.email, '— writing cart note for Flow pickup');
    writeCartNote(data);
  }

  function makeSessionId() {
    return 'aq_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 7);
  }

  function parsePrice(str) {
    return parseFloat((str || '0').replace(/[^0-9.]/g, '')) || 0;
  }

  window.ANQ_TRACK = {

    sessionId: makeSessionId(),

    quizStarted: function () {
      gaPush('anq_started', {
        quiz_session_id: this.sessionId
      });
    },

    questionAnswered: function (questionId, selectedValues) {
      gaPush('anq_question_answered', {
        quiz_session_id:   this.sessionId,
        quiz_question_id:  questionId,
        quiz_answers:      selectedValues.join(','),
        quiz_answer_count: selectedValues.length
      });
    },

    quizCompleted: function (answers, persona, productsShown) {
      var self = this;

      var productHandles = (productsShown || []).map(function (p) {
        return p.handle || p.name;
      });

      var data = {
        sessionId:     self.sessionId,
        skin:          answers.skin               || [],
        hair:          answers.hair               || [],
        age:           (answers.age || [])[0]     || '',    /* ← added */
        lifestyle:     answers.lifestyle          || [],
        persona:       persona                    || '',
        productsShown: productHandles
      };

      /* GA4 — single event with all answers including age */
      gaPush('anq_completed', {
        quiz_session_id:     self.sessionId,
        quiz_skin_answers:   data.skin.join(','),
        quiz_hair_answers:   data.hair.join(','),
        quiz_age:            data.age,                      /* ← added */
        quiz_lifestyle:      data.lifestyle.join(','),
        quiz_persona:        data.persona,
        quiz_products_shown: data.productsShown.join(','),
        quiz_product_count:  productHandles.length
      });

      writeCartAttributes(data);
      writeCustomerData(data);

      this._lastData = data;
    },

    productsShown: function (products) {
      var total = products.reduce(function (s, p) { return s + parsePrice(p.price); }, 0);
      gaPush('anq_products_shown', {
        quiz_session_id:   this.sessionId,
        quiz_products:     products.map(function (p) { return p.handle || p.name; }).join(','),
        quiz_total_value:  total,
        quiz_currency:     'INR'
      });
    },

    addToCart: function (product) {
      gaPush('anq_add_to_cart', {
        quiz_session_id: this.sessionId,
        quiz_product:    product.handle || product.name,
        quiz_variant_id: product.variantId,
        quiz_price:      parsePrice(product.price),
        quiz_currency:   'INR'
      });
    },

    addAllToCart: function (products, totalValue) {
      gaPush('anq_add_all_to_cart', {
        quiz_session_id:    this.sessionId,
        quiz_products:      products.map(function (p) { return p.handle || p.name; }).join(','),
        quiz_total_value:   totalValue,
        quiz_product_count: products.length,
        quiz_currency:      'INR'
      });
    },

    viewProduct: function (product) {
      gaPush('anq_view_product', {
        quiz_session_id: this.sessionId,
        quiz_product:    product.handle || product.name,
        quiz_url:        product.url
      });
    },

    quizRestarted: function () {
      gaPush('anq_restarted', {
        quiz_session_id: this.sessionId
      });
      this.sessionId = makeSessionId();
    }

  };

  log('ANQ_TRACK ready. Session:', window.ANQ_TRACK.sessionId);

})();

/* ═══════════════════════════════════════════════════════════
   SHOPIFY FLOW SETUP (run once, no code needed after)
   ═══════════════════════════════════════════════════════════

   Shopify Admin → Flow → Create workflow:

   TRIGGER:   Order created
   CONDITION: Order attribute "quiz_skin" is not blank
   ACTION:    Update customer metafield
              Namespace: antinorm_quiz
              Key:       last_quiz
              Type:      json
              Value:
              {
                "skin":           "{{ order.attributes.quiz_skin }}",
                "hair":           "{{ order.attributes.quiz_hair }}",
                "age":            "{{ order.attributes.quiz_age }}",
                "lifestyle":      "{{ order.attributes.quiz_lifestyle }}",
                "persona":        "{{ order.attributes.quiz_persona }}",
                "products_shown": "{{ order.attributes.quiz_products_shown }}",
                "completed_at":   "{{ order.attributes.quiz_completed_at }}",
                "order_id":       "{{ order.id }}"
              }
   ═══════════════════════════════════════════════════════════ */
